import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { Schema } from '@/shared/api/contract';
import type { ApiError } from '@/shared/api/errors';
import { emailDetailKey } from '@/shared/emails/api';

import { quoteDetailKey, quoteListsKey, sharedQuotesApi } from './api';

/**
 * «Rehacer» a quote. The API answers with the **new** draft, so its detail is seeded
 * from the answer and the caller goes to its editor; the source's entry is left alone.
 * The lists change (a new row), and so does the email behind it: since contract v10.7
 * an email's `quote_id` is its newest quote, which is now this one.
 */
export function useDuplicateQuote(sourceId: string) {
  const queryClient = useQueryClient();
  return useMutation<Schema<'QuoteRead'>, ApiError, void>({
    mutationFn: () => sharedQuotesApi.duplicate(sourceId),
    onSuccess: (quote) => {
      queryClient.setQueryData(quoteDetailKey(quote.id), quote);
      void queryClient.invalidateQueries({ queryKey: quoteListsKey });
      if (quote.email_id) {
        void queryClient.invalidateQueries({ queryKey: emailDetailKey(quote.email_id) });
      }
    },
  });
}
